import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { TextField } from '@mui/material';
import { state } from '__common/store';

type Props = {
  value: string | number,
  onConfirm?: Function,
  onBlur?: (event: any) => void,
  onCancel?: Function,
  textFieldProps?: Object,
  shouldConfirm: boolean,
  shouldConfirmCb?: (val) => boolean
  title: string,
  content: string,
  applyOnBlur?: boolean,
  disabled?: boolean,
  compactConfirm?: boolean,
};

type State = {
  value: string,
  tempValue: string,
  confirmVisible: boolean,
};

export default class ConfirmationTextField extends React.Component<Props, State> {
  skipNextBlurCommit = false;

  constructor(props) {
    super(props);
    this.state = {
      value: props.value,
      tempValue: '',
      confirmVisible: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.value !== this.props.value && this.state.value !== this.props.value) {
      this.setState({ value: String(this.props.value), tempValue: this.state.value, confirmVisible: false });
    }
  }

  valuesEqual = (a: string | number, b: string | number) => {
    if (String(a) === String(b)) {
      return true;
    }
    const na = Number(a);
    const nb = Number(b);
    return a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
  }

  commitValue = (value: string) => {
    const { shouldConfirm, onConfirm, shouldConfirmCb } = this.props;
    if (this.valuesEqual(value, this.props.value)) {
      this.setState({ value, confirmVisible: false });
      return;
    }
    const confirmed = shouldConfirm && (!shouldConfirmCb || shouldConfirmCb(value));
    if (confirmed) {
      this.setState({ value, tempValue: String(this.props.value), confirmVisible: true });
    } else {
      this.setState({ value, tempValue: String(this.props.value), confirmVisible: false });
      if (onConfirm) {
        onConfirm(value);
      }
    }
  }

  handleFieldChange = (value: string) => {
    if (this.props.applyOnBlur) {
      this.setState({ value });
      return;
    }
    this.commitValue(value);
  }

  handleBlur = (event: any) => {
    if (this.skipNextBlurCommit) {
      this.skipNextBlurCommit = false;
    } else if (this.props.applyOnBlur && !this.state.confirmVisible) {
      this.commitValue(this.state.value);
    }
    this.props.onBlur?.(event);
  }

  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const textFieldProps = (this.props.textFieldProps || {}) as { onKeyDown?: Function };
    if (this.props.applyOnBlur && event.key === 'Enter') {
      event.preventDefault();
      this.commitValue(this.state.value);
      this.skipNextBlurCommit = true;
      (event.target as HTMLInputElement).blur();
    }
    textFieldProps.onKeyDown?.(event);
  }

  confirm = () => {
    const { onConfirm } = this.props;
    this.setState({ tempValue: this.state.value, confirmVisible: false });

    if (onConfirm) {
      onConfirm(this.state.value);
    }
  }

  cancel = () => {
    const { onCancel } = this.props;
    this.setState({ value: this.state.tempValue, confirmVisible: false });

    if (onCancel) {
      onCancel(this.state.tempValue);
    }
  }

  render() {
    const { textFieldProps, title, content, disabled, compactConfirm = true } = this.props;
    const { value, confirmVisible } = this.state;
    const {projectConfiguration:{projectEnvConfig}} = state();
    const {
      onKeyDown: _onKeyDown,
      onBlur: textFieldOnBlur,
      ...restTextFieldProps
    } = (textFieldProps || {}) as {
      onKeyDown?: Function,
      onBlur?: Function,
      [k: string]: any,
    };
    return (
      <>
        <TextField
          id=""
          value={value}
          variant="standard"
          {...restTextFieldProps}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleFieldChange(event.target.value)}
          onBlur={(event) => {
            this.handleBlur(event);
            textFieldOnBlur?.(event);
          }}
          onKeyDown={this.handleKeyDown}
          disabled={disabled || projectEnvConfig?.is_aurora_project || projectEnvConfig?.is_os_project}
        />
        <Dialog
          id="arrays-lost-modal"
          className={compactConfirm ? 'layout-confirm-dialog' : undefined}
          open={confirmVisible}
          onClose={this.cancel}
          maxWidth={compactConfirm ? false : undefined}
          PaperProps={compactConfirm ? { className: 'layout-confirm-dialog-paper' } : undefined}
        >
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {content}
          </DialogContent>
          <DialogActions>
            <Button 
              className="cancel-button" 
              onClick={this.cancel}
              variant="text"
            >
              NO, CANCEL
            </Button>
            <Button 
              className="submit-button" 
              onClick={this.confirm}
              variant="text"
            >
              YES, CONTINUE
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}
